import React, { useState, useEffect } from 'react';
import { apiService, Offering, OfferingData, RedemptionMode } from '../services/api';
import { uploadImageToS3, isS3Configured } from '../services/s3Upload';
import { DragDropImageUpload } from './DragDropImageUpload';
import './OfferingsManager.css';

// Character limits (FE responsibility - backend caps name/code at 200 chars)
const NAME_MAX_LENGTH = 200;
const CODE_MAX_LENGTH = 200;
const DESCRIPTION_MAX_LENGTH = 2000;

// Redemption mode options for the offering form
const REDEMPTION_MODES: { value: RedemptionMode; label: string; description: string }[] = [
  { value: 'in_store', label: 'In-store', description: 'Redeemed in person with a code/timer' },
  { value: 'webpage', label: 'Website', description: 'Sends customers to a web page' },
  { value: 'url_with_code', label: 'Website + code', description: 'Opens a URL with the discount code embedded' },
];

const OFFERING_TYPES = [
  'Discount',
  'Free Item',
  'BOGO',
  'Gift Card',
  'Service',
  'Event Ticket',
  'Other'
];

// Distance scope options with their meter values
const DISTANCE_SCOPES = {
  local: { label: 'Local', value: 10000, description: '~10 km radius' },
  provincial: { label: 'Provincial', value: 100000, description: '~100 km radius' },
  national: { label: 'National', value: 2500000, description: '~2,500 km radius' },
  global: { label: 'Global', value: 100000000, description: 'Worldwide (no limit)' },
} as const;

type DistanceScope = keyof typeof DISTANCE_SCOPES;

// Dynamic label/placeholder based on offering type
const getAmountConfig = (type: string): { label: string; placeholder: string } => {
  switch (type) {
    case 'Discount':
      return { label: 'Discount Amount', placeholder: 'e.g., 10 (for 10% or $10 off)' };
    case 'Gift Card':
      return { label: 'Gift Card Value', placeholder: 'e.g., 25 (for $25 value)' };
    case 'Free Item':
      return { label: 'Item Value', placeholder: 'e.g., 15 (estimated value)' };
    case 'BOGO':
      return { label: 'Savings Value', placeholder: 'e.g., 20 (value of free item)' };
    case 'Service':
      return { label: 'Service Value', placeholder: 'e.g., 50 (service price)' };
    case 'Event Ticket':
      return { label: 'Ticket Price', placeholder: 'e.g., 75 (ticket value)' };
    default:
      return { label: 'Amount/Value', placeholder: 'e.g., 10' };
  }
};

interface LocationOption {
  id: number;
  address: string;
}

// Mobile Preview Component
const MobilePreview: React.FC<{
  name: string;
  description: string;
  offeringType: string;
  amount: string;
  expirationDate: string;
  previewImage: string;
  isEcom: boolean;
  redemptionCode: string;
  noExpiration: boolean;
}> = ({ name, description, offeringType, amount, expirationDate, previewImage, isEcom, redemptionCode, noExpiration }) => {
  const formatExpiry = (dateString: string) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="mobile-preview-container">
      <div className="mobile-preview-header">
        <span className="preview-icon">📱</span>
        <span className="preview-title">Live Preview</span>
      </div>
      <div className="mobile-frame">
        <div className="mobile-notch"></div>
        <div className="mobile-screen">
          {/* Offer Card Preview */}
          <div className="preview-card">
            {/* Image/Placeholder */}
            <div className="preview-card-image">
              {previewImage ? (
                <img src={previewImage} alt="Offer" />
              ) : (
                <div className="preview-image-placeholder">
                  <span>🎁</span>
                </div>
              )}
              {/* Active Badge */}
              <div className="preview-active-badge">
                <span className="preview-dot"></span>
                Active
              </div>
            </div>
            
            {/* Content */}
            <div className="preview-card-content">
              <h4 className="preview-offer-name">
                {name || 'Offer Name'}
              </h4>
              <p className="preview-offer-desc">
                {description || 'Add a description to see how it will appear...'}
              </p>
              
              {/* Type Badge */}
              <div className="preview-tags">
                <span className="preview-type-badge">{offeringType}</span>
                {/* {amount && (
                  <span className="preview-amount-badge">
                    {offeringType === 'Discount' ? `${amount}% off` : `$${amount}`}
                  </span>
                )} */}
              </div>
              
              {/* Expiry */}
              {noExpiration ? (
                <div className="preview-expiry preview-no-expiry">
                  <span>✓</span> No expiration date
                </div>
              ) : expirationDate && (
                <div className="preview-expiry">
                  <span>⏰</span> Expires {formatExpiry(expirationDate)}
                </div>
              )}
              
              {/* Redeem Button */}
              <button className="preview-redeem-btn">
                Redeem Offer →
              </button>
            </div>
          </div>

          {/* Code Preview (if ecom) */}
          {isEcom && (
            <div className="preview-code-section">
              <div className="preview-code-label">Promo Code</div>
              <div className="preview-code-box">
                {redemptionCode || 'YOURCODE'}
              </div>
              <div className="preview-code-hint">Show this code at checkout</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const OfferingsManager: React.FC = () => {
  const [offerings, setOfferings] = useState<Offering[]>([]);
  const [locations, setLocations] = useState<LocationOption[]>([]);
  const [isVirtualBusiness, setIsVirtualBusiness] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingOffering, setEditingOffering] = useState<Offering | null>(null);
  
  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [image, setImage] = useState('');
  const [previewImage, setPreviewImage] = useState('');
  const [offeringType, setOfferingType] = useState(OFFERING_TYPES[0]);
  const [expirationDate, setExpirationDate] = useState('');
  const [amount, setAmount] = useState('');
  const [distanceScope, setDistanceScope] = useState<DistanceScope>('local');
  const [redemptionMode, setRedemptionMode] = useState<RedemptionMode>('in_store');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [redemptionCode, setRedemptionCode] = useState('');
  const [selectedLocationIds, setSelectedLocationIds] = useState<number[]>([]);
  const [noExpiration, setNoExpiration] = useState(false);
  // Single-use is the exception; deals are unlimited-use by default.
  const [isSingleUse, setIsSingleUse] = useState(false);
  
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);

  // Get dynamic amount config based on offering type
  const amountConfig = getAmountConfig(offeringType);

  // Derived redemption-mode flags
  const isEcom = redemptionMode !== 'in_store';
  const requiresWebsiteUrl =
    redemptionMode === 'webpage' || redemptionMode === 'url_with_code';
  const requiresCode = redemptionMode === 'url_with_code';

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch offerings and business (for locations) in parallel
      const [offeringsData, businessData] = await Promise.all([
        apiService.getOfferings(),
        apiService.getBusiness()
      ]);
      
      setOfferings(offeringsData);
      
      // Extract locations from business using their actual IDs from the API
      if (businessData?.locations) {
        const locationOptions = businessData.locations
          .filter(loc => loc.id !== undefined)
          .map(loc => ({
            id: loc.id as number,
            address: loc.address
          }));
        setLocations(locationOptions);
        
        // Check if this is a virtual-only business (all locations at 0,0 or marked as virtual)
        const isVirtual = businessData.locations.length > 0 && businessData.locations.every(loc => 
          (loc.latitude === 0 && loc.longitude === 0) || 
          loc.address?.toLowerCase().includes('virtual')
        );
        setIsVirtualBusiness(isVirtual);
      }
    } catch (err) {
      console.error('Failed to fetch data:', err);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const showSuccessMessage = (message: string) => {
    setSuccess(message);
    setTimeout(() => setSuccess(null), 3000);
  };

  const resetForm = () => {
    setName('');
    setDescription('');
    setImage('');
    setPreviewImage('');
    setOfferingType(OFFERING_TYPES[0]);
    setExpirationDate('');
    setNoExpiration(false);
    setAmount('');
    // Virtual businesses must use Global reach
    setDistanceScope(isVirtualBusiness ? 'global' : 'local');
    setRedemptionMode('in_store');
    setWebsiteUrl('');
    setRedemptionCode('');
    setSelectedLocationIds([]);
    setIsSingleUse(false);
    setEditingOffering(null);
    setError(null);
  };

  const handleImageChange = async (file: File) => {
    // Show local preview immediately
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result as string;
      setPreviewImage(result);
    };
    reader.readAsDataURL(file);

    // Upload to S3
    setUploadingImage(true);
    setError(null);

    try {
      if (!isS3Configured()) {
        throw new Error('S3 is not configured. Please check your environment variables.');
      }

      const s3Url = await uploadImageToS3(file);
      setImage(s3Url);
      showSuccessMessage('Image uploaded successfully');
    } catch (err: any) {
      console.error('Image upload failed:', err);
      setError(err.message || 'Failed to upload image. Please try again.');
      setPreviewImage('');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Client-side validation (see FE Integration Guide sections 1 & 6)
    if (name.length > NAME_MAX_LENGTH) {
      setError(`Offering name must be ${NAME_MAX_LENGTH} characters or fewer.`);
      return;
    }
    if (description.length > DESCRIPTION_MAX_LENGTH) {
      setError(`Description must be ${DESCRIPTION_MAX_LENGTH} characters or fewer.`);
      return;
    }
    if (requiresWebsiteUrl && !websiteUrl.trim()) {
      setError('A website URL is required for website-based redemption modes.');
      return;
    }
    if (requiresCode && !redemptionCode.trim()) {
      setError('A discount code is required for the "Website + code" redemption mode.');
      return;
    }
    if (redemptionCode && redemptionCode.length > CODE_MAX_LENGTH) {
      setError(`Discount code must be ${CODE_MAX_LENGTH} characters or fewer.`);
      return;
    }

    setSubmitting(true);
    setError(null);

    // Get distance from the selected scope
    const finalDistance = DISTANCE_SCOPES[distanceScope].value;
    
    // Use Jan 1 2100 as the "no expiration" date
    const finalExpirationDate = noExpiration 
      ? new Date('2100-01-01').toISOString()
      : (expirationDate ? new Date(expirationDate).toISOString() : undefined);
    
    const offeringData: OfferingData = {
      name,
      description,
      // Image is now S3 URL, not base64
      image: image || '',
      type: offeringType,
      expiration_date: finalExpirationDate,
      amount: amount ? parseFloat(amount) : 0,
      distance: finalDistance,
      redemption_mode: redemptionMode,
      website_url: requiresWebsiteUrl ? websiteUrl.trim() : undefined,
      // is_ecom kept for backward compatibility (derived from the mode)
      is_ecom: isEcom,
      code: requiresCode ? redemptionCode : (isEcom ? redemptionCode : undefined),
      is_single_use: isSingleUse,
      location_ids: selectedLocationIds
    };

    try {
      if (editingOffering) {
        // Update existing offering
        const updated = await apiService.updateOffering(editingOffering.id, offeringData);
        // Merge the submitted data with the response to preserve fields the backend doesn't return
        const mergedOffering = {
          ...updated,
          is_ecom: offeringData.is_ecom,
          code: offeringData.code,
          redemption_mode: offeringData.redemption_mode,
          website_url: offeringData.website_url,
          is_single_use: offeringData.is_single_use,
        };
        setOfferings(prev => prev.map(o => o.id === updated.id ? mergedOffering : o));
        showSuccessMessage('Offering updated successfully');
      } else {
        // Create new offering
        const created = await apiService.createOffering(offeringData);
        // Merge the submitted data with the response to preserve fields the backend doesn't return
        const mergedOffering = {
          ...created,
          is_ecom: offeringData.is_ecom,
          code: offeringData.code,
          redemption_mode: offeringData.redemption_mode,
          website_url: offeringData.website_url,
          is_single_use: offeringData.is_single_use,
        };
        setOfferings(prev => [...prev, mergedOffering]);
        showSuccessMessage('Offering created successfully');
      }
      resetForm();
      setShowForm(false);
    } catch (err: any) {
      console.error('Failed to save offering:', err);
      setError(err.message || 'Failed to save offering');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (offering: Offering) => {
    setEditingOffering(offering);
    setName(offering.name);
    setDescription(offering.description || '');
    setImage(offering.image || '');
    setPreviewImage(offering.image || '');
    setOfferingType(offering.type || OFFERING_TYPES[0]);
    
    // Check if this is a "no expiration" date (year > 2050)
    const expiryDateStr = offering.expiration_date ? offering.expiration_date.split('T')[0] : '';
    const expiryYear = offering.expiration_date ? new Date(offering.expiration_date).getFullYear() : 0;
    const isNoExpiration = expiryYear > 2050;
    setNoExpiration(isNoExpiration);
    setExpirationDate(isNoExpiration ? '' : expiryDateStr);
    setAmount(offering.amount?.toString() || '');
    
    // Determine distance scope from the stored distance value
    // Virtual businesses must always use Global scope
    if (isVirtualBusiness) {
      setDistanceScope('global');
    } else {
      const offeringDistance = offering.distance || 0;
      if (offeringDistance >= DISTANCE_SCOPES.global.value) {
        setDistanceScope('global');
      } else if (offeringDistance >= DISTANCE_SCOPES.national.value) {
        setDistanceScope('national');
      } else if (offeringDistance >= DISTANCE_SCOPES.provincial.value) {
        setDistanceScope('provincial');
      } else {
        setDistanceScope('local');
      }
    }
    
    // Redemption settings - prefer redemption_mode, fall back to legacy is_ecom
    const mode: RedemptionMode =
      offering.redemption_mode ||
      (offering.is_ecom ? 'url_with_code' : 'in_store');
    setRedemptionMode(mode);
    setWebsiteUrl(offering.website_url || '');
    setRedemptionCode(offering.code || '');
    setIsSingleUse(offering.is_single_use === true);
    
    // Handle both location_ids (array of numbers) and locations (array of objects with id)
    let locationIds: number[] = [];
    if (offering.location_ids && offering.location_ids.length > 0) {
      locationIds = offering.location_ids;
    } else if (offering.locations && offering.locations.length > 0) {
      locationIds = offering.locations.map(loc => loc.id);
    }
    setSelectedLocationIds(locationIds);
    
    setShowForm(true);
  };

  const handleLocationToggle = (locationId: number) => {
    setSelectedLocationIds(prev => {
      if (prev.includes(locationId)) {
        return prev.filter(id => id !== locationId);
      } else {
        return [...prev, locationId];
      }
    });
  };

  const handleSelectAllLocations = () => {
    if (selectedLocationIds.length === locations.length) {
      setSelectedLocationIds([]);
    } else {
      setSelectedLocationIds(locations.map(loc => loc.id));
    }
  };

  const handleDelete = async (id: number) => {
    setSubmitting(true);
    try {
      await apiService.deleteOffering(id);
      setOfferings(prev => prev.filter(o => o.id !== id));
      showSuccessMessage('Offering deleted');
      setDeleteConfirmId(null);
    } catch (err: any) {
      console.error('Failed to delete offering:', err);
      setError(err.message || 'Failed to delete offering');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    resetForm();
    setShowForm(false);
  };

  if (loading) {
    return (
      <div className="offerings-manager">
        <div className="loading-state">
          <p>Loading offerings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="offerings-manager">
      <div className="info-card">
        <div className="info-card-icon">🎁</div>
        <div className="info-card-content">
          <h3>Offerings</h3>
          <p>Create discounts, free items, and special deals to attract and reward your customers.</p>
        </div>
        {!showForm && (
          <button
            className="info-card-btn"
            onClick={() => {
              resetForm();
              setShowForm(true);
            }}
          >
            + Add Offering
          </button>
        )}
      </div>

      {success && <div className="success-message">✓ {success}</div>}
      {error && !showForm && <div className="error-message">⚠ {error}</div>}

      {showForm && (
        <div className="form-with-preview">
          <form onSubmit={handleSubmit} className="offering-form">
            <div className="form-header">
              <h3>{editingOffering ? '✏️ Edit Offering' : '🎁 Create New Offering'}</h3>
            </div>

            <div className="form-body">
              {error && <div className="error-message">⚠ {error}</div>}
              
              <div className="form-section">
                <div className="form-section-title">Offering Details</div>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="offeringName">Offering Name *</label>
                    <input
                      type="text"
                      id="offeringName"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="e.g., 10% Off First Purchase"
                      required
                      maxLength={NAME_MAX_LENGTH}
                      disabled={submitting}
                    />
                    <span className="field-hint">{name.length}/{NAME_MAX_LENGTH}</span>
                  </div>

                  <div className="form-group">
                    <label htmlFor="offeringType">Offering Type *</label>
                    <select
                      id="offeringType"
                      value={offeringType}
                      onChange={(e) => setOfferingType(e.target.value)}
                      required
                      disabled={submitting}
                    >
                      {OFFERING_TYPES.map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="amount">{amountConfig.label}</label>
                    <input
                      type="number"
                      id="amount"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      placeholder={amountConfig.placeholder}
                      step="any"
                      disabled={submitting}
                    />
                    <span className="field-hint">
                      {offeringType === 'Discount' && 'Enter percentage (10) or dollar amount'}
                      {offeringType === 'Gift Card' && 'Enter the gift card dollar value'}
                    </span>
                  </div>

                  <div className="form-group">
                    <label>Offering Reach</label>
                    {isVirtualBusiness && (
                      <div className="virtual-business-notice">
                        <span className="notice-icon">🌐</span>
                        <span>Virtual businesses automatically use <strong>Global</strong> reach so customers worldwide can see your offerings.</span>
                      </div>
                    )}
                    <div className="distance-scope-options">
                      {(Object.keys(DISTANCE_SCOPES) as DistanceScope[]).map((scope) => {
                        // For virtual businesses, only Global is allowed
                        const isDisabledForVirtual = isVirtualBusiness && scope !== 'global';
                        return (
                          <label 
                            key={scope} 
                            className={`scope-option ${distanceScope === scope ? 'selected' : ''} ${isDisabledForVirtual ? 'disabled' : ''}`}
                          >
                            <input
                              type="radio"
                              name="distanceScope"
                              value={scope}
                              checked={distanceScope === scope}
                              onChange={(e) => setDistanceScope(e.target.value as DistanceScope)}
                              disabled={submitting || isDisabledForVirtual}
                            />
                            <div className="scope-content">
                              <span className="scope-label">{DISTANCE_SCOPES[scope].label}</span>
                              <span className="scope-description">{DISTANCE_SCOPES[scope].description}</span>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="expirationDate">Expiration Date</label>
                    <div className="no-expiration-toggle">
                      <label className="toggle-switch">
                        <input
                          type="checkbox"
                          checked={noExpiration}
                          onChange={(e) => {
                            setNoExpiration(e.target.checked);
                            if (e.target.checked) {
                              setExpirationDate('');
                            }
                          }}
                          disabled={submitting}
                        />
                        <span className="toggle-slider"></span>
                      </label>
                      <span className="toggle-label">No expiration date</span>
                    </div>
                    {!noExpiration && (
                      <input
                        type="date"
                        id="expirationDate"
                        value={expirationDate}
                        onChange={(e) => setExpirationDate(e.target.value)}
                        disabled={submitting}
                      />
                    )}
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="redemptionMode">Redemption Method *</label>
                    <select
                      id="redemptionMode"
                      value={redemptionMode}
                      onChange={(e) => {
                        const mode = e.target.value as RedemptionMode;
                        setRedemptionMode(mode);
                        // Clear fields that no longer apply
                        if (mode === 'in_store') {
                          setWebsiteUrl('');
                          setRedemptionCode('');
                        } else if (mode === 'webpage') {
                          setRedemptionCode('');
                        }
                      }}
                      disabled={submitting}
                    >
                      {REDEMPTION_MODES.map(mode => (
                        <option key={mode.value} value={mode.value}>{mode.label}</option>
                      ))}
                    </select>
                    <span className="field-hint">
                      {REDEMPTION_MODES.find(m => m.value === redemptionMode)?.description}
                    </span>
                  </div>

                  {requiresWebsiteUrl && (
                    <div className="form-group">
                      <label htmlFor="websiteUrl">
                        Website URL *
                        <span className="label-hint">
                          {redemptionMode === 'url_with_code'
                            ? 'Use {code} as a placeholder, or the code will be appended'
                            : 'Where customers are sent to redeem'}
                        </span>
                      </label>
                      <input
                        type="url"
                        id="websiteUrl"
                        value={websiteUrl}
                        onChange={(e) => setWebsiteUrl(e.target.value)}
                        placeholder="https://shop.example.com/deal"
                        disabled={submitting}
                      />
                    </div>
                  )}

                  {requiresCode && (
                    <div className="form-group">
                      <label htmlFor="redemptionCode">
                        Discount Code *
                        <span className="label-hint">Injected into the URL / used at checkout</span>
                      </label>
                      <input
                        type="text"
                        id="redemptionCode"
                        value={redemptionCode}
                        onChange={(e) => setRedemptionCode(e.target.value.toUpperCase())}
                        placeholder="e.g., SAVE10"
                        maxLength={CODE_MAX_LENGTH}
                        disabled={submitting}
                        className="promo-code-input"
                      />
                    </div>
                  )}
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Redemption Limit</label>
                    <div className="no-expiration-toggle">
                      <label className="toggle-switch">
                        <input
                          type="checkbox"
                          checked={isSingleUse}
                          onChange={(e) => setIsSingleUse(e.target.checked)}
                          disabled={submitting}
                        />
                        <span className="toggle-slider"></span>
                      </label>
                      <span className="toggle-label">One-time use only (single redemption per user)</span>
                    </div>
                    <span className="field-hint">
                      Leave off so customers can redeem this deal an unlimited number of times. Turn on only for exclusive, single-use offers.
                    </span>
                  </div>
                </div>
              </div>

              <div className="form-section">
                <div className="form-section-title">Description</div>
                <div className="form-group">
                  <label htmlFor="description">Tell customers about this offering *</label>
                  <textarea
                    id="description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Describe what customers will receive, any terms or conditions, and why they should take advantage of this offer..."
                    required
                    maxLength={DESCRIPTION_MAX_LENGTH}
                    disabled={submitting}
                  />
                  <span className="field-hint">{description.length}/{DESCRIPTION_MAX_LENGTH}</span>
                </div>
              </div>

              <div className="form-section">
                <div className="form-section-title">Available Locations</div>
                <div className="form-group">
                  <label>Select which locations this offering is available at</label>
                  {locations.length === 0 ? (
                    <div className="no-locations-message">
                      No locations set up yet. Add locations in the Business Details tab first.
                    </div>
                  ) : (
                    <div className="location-multi-select">
                      <div className="select-all-row">
                        <label className="checkbox-label">
                          <input
                            type="checkbox"
                            checked={selectedLocationIds.length === locations.length && locations.length > 0}
                            onChange={handleSelectAllLocations}
                            disabled={submitting}
                          />
                          <span>Select All Locations</span>
                        </label>
                        <span className="selected-count">
                          {selectedLocationIds.length} of {locations.length} selected
                        </span>
                      </div>
                      <div className="location-checkboxes">
                        {locations.map(location => (
                          <label key={location.id} className="checkbox-label location-option">
                            <input
                              type="checkbox"
                              checked={selectedLocationIds.includes(location.id)}
                              onChange={() => handleLocationToggle(location.id)}
                              disabled={submitting}
                            />
                            <span className="location-address">📍 {location.address}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="form-section">
                <div className="form-section-title">Image</div>
                <div className="form-group">
                  <DragDropImageUpload
                    label="Offering Image (optional)"
                    onFileSelect={handleImageChange}
                    previewUrl={previewImage}
                    uploading={uploadingImage}
                    placeholder="Drop image here or click to browse"
                  />
                </div>
              </div>
            </div>

            <div className="form-actions">
              <button type="button" className="cancel-button" onClick={handleCancel} disabled={submitting}>
                Cancel
              </button>
              <button type="submit" className="submit-button" disabled={submitting}>
                {submitting ? 'Saving...' : (editingOffering ? 'Update Offering' : 'Create Offering')}
              </button>
            </div>
          </form>

          {/* Live Preview */}
          <MobilePreview
            name={name}
            description={description}
            offeringType={offeringType}
            amount={amount}
            expirationDate={expirationDate}
            previewImage={previewImage}
            isEcom={isEcom}
            redemptionCode={redemptionCode}
            noExpiration={noExpiration}
          />
        </div>
      )}

      {!showForm && (
        <div className="offerings-list">
          {/* Warning for offerings without locations */}
          {offerings.length > 0 && offerings.some(o => {
            const locationIds = o.location_ids || (o.locations?.map(l => l.id) || []);
            return locationIds.length === 0;
          }) && (
            <div className="no-locations-warning">
              <span className="warning-icon">⚠️</span>
              <div className="warning-content">
                <strong>Some offerings have no locations assigned.</strong>
                <p>Offerings without locations won't appear in location-based searches (like "Hot Deals"). 
                   Edit the offering and select at least one location to maximize visibility.</p>
              </div>
            </div>
          )}
          
          {offerings.length === 0 ? (
            <div className="empty-state">
              <p>No offerings yet. Click "+ Add Offering" to create your first one.</p>
            </div>
          ) : (
            <table className="offerings-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Type</th>
                  <th>Locations</th>
                  <th>Expires</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {offerings.map(offering => {
                  // Get location addresses for this offering
                  const offeringLocationIds = offering.location_ids || 
                    (offering.locations?.map(l => l.id) || []);
                  const offeringLocations = locations.filter(loc => 
                    offeringLocationIds.includes(loc.id)
                  );
                  
                  return (
                  <tr key={offering.id}>
                    <td className="name-cell">
                      {offering.image && (
                        <img src={offering.image} alt="" className="offering-thumbnail" />
                      )}
                      <div>
                        <span className="offering-name">{offering.name}</span>
                        <span className="offering-desc">{offering.description}</span>
                      </div>
                    </td>
                    <td>
                      <span className="offering-type-badge">{offering.type}</span>
                    </td>
                    <td className="locations-cell">
                      {offeringLocations.length === 0 ? (
                        <span className="no-locations">No locations</span>
                      ) : offeringLocations.length === locations.length ? (
                        <span className="all-locations-badge">📍 All locations</span>
                      ) : (
                        <div className="location-tags">
                          {offeringLocations.slice(0, 2).map(loc => (
                            <span key={loc.id} className="location-tag">
                              📍 {loc.address.length > 25 ? loc.address.substring(0, 25) + '...' : loc.address}
                            </span>
                          ))}
                          {offeringLocations.length > 2 && (
                            <span className="more-locations">+{offeringLocations.length - 2} more</span>
                          )}
                        </div>
                      )}
                    </td>
                    <td>
                      {offering.expiration_date 
                        ? (new Date(offering.expiration_date).getFullYear() > 2050
                            ? <span className="no-expiry-badge">No expiration</span>
                            : new Date(offering.expiration_date).toLocaleDateString())
                        : '-'}
                    </td>
                    <td className="actions-cell">
                      {deleteConfirmId === offering.id ? (
                        <>
                          <button 
                            className="action-btn confirm-btn"
                            onClick={() => handleDelete(offering.id)}
                            disabled={submitting}
                          >
                            Confirm
                          </button>
                          <button 
                            className="action-btn cancel-btn"
                            onClick={() => setDeleteConfirmId(null)}
                          >
                            Cancel
                          </button>
                        </>
                      ) : (
                        <>
                          <button 
                            className="action-btn edit-btn"
                            onClick={() => handleEdit(offering)}
                          >
                            Edit
                          </button>
                          <button 
                            className="action-btn delete-btn"
                            onClick={() => setDeleteConfirmId(offering.id)}
                          >
                            Delete
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
};

export default OfferingsManager;
